<script>
import SimulatorOfficialDetailComponent from '../../features/details/SimulatorOfficialDetailComponent';
import SimulatorDetailSectionView from '../../views/details/SimulatorDetailSectionView';
import SimulatorOfficialResultView from '../../views/details/SimulatorOfficialResultView';

export default {
  name: 'SimulatorOfficialDetailView',
  components: {
    'simulator-detail-section-view': SimulatorDetailSectionView,
    'simulator-official-result-view': SimulatorOfficialResultView,
  },
  extends: SimulatorOfficialDetailComponent,
  computed: {
    // La barra de retorno respeta la jerarquía: con una sección abierta
    // regresa al detalle del simulador; sin sección, sale a la lista.
    backLabel() {
      return this.sectionSelected ? 'Volver al simulador' : 'Volver a Simuladores';
    },
  },
};
</script>
<template>
  <div class="sim_apply_page">
    <!--
      El simulador se muestra a pantalla completa, sin el sidebar del
      dashboard: durante la evaluación no debe haber elementos que distraigan.
      La única salida es la barra de retorno, que sube un nivel en la
      jerarquía (sección → detalle del simulador → lista de simuladores).
    -->
    <div class="wrapper is-fullpage">
      <div class="sa4d25">
        <div class="container-fluid">
          <div class="section3125">
            <!-- Barra de retorno -->
            <div class="course_back_bar">
              <button
                type="button"
                class="course_back_btn"
                @click="onBack"
              >
                <i class="uil uil-arrow-left" />
                <span>{{ backLabel }}</span>
              </button>
            </div>

            <template v-if="simulator && !showResultView">
              <!-- Encabezado de página -->
              <div class="stream_page_header">
                <div class="stream_header_left">
                  <div class="stream_header_icon">
                    <i class="fas fa-laptop-code" />
                  </div>
                  <div class="stream_header_text">
                    <h4 class="item_title">
                      {{ simulator.name }}
                    </h4>
                    <p class="stream_header_subtitle">
                      {{ simulator.description || 'Evaluación oficial de admisión con retroalimentación de respuestas.' }}
                    </p>
                  </div>
                </div>
                <div class="stream_header_right">
                  <span
                    v-if="simulator.isTimer && timerLabel"
                    class="sim_apply_timer_pill"
                  >
                    <i class="uil uil-stopwatch" /> {{ timerLabel }}
                  </span>
                  <span
                    v-else
                    class="stream_live_pill"
                  >
                    <i class="uil uil-clock-three mr-1" />
                    {{ simulator.isTimer ? 'Con cronómetro' : 'Práctica libre' }}
                  </span>
                </div>
              </div>

              <!-- Resumen: portada + ficha del examen -->
              <div
                v-if="!sectionSelected"
                class="sim_apply_summary"
              >
                <div class="sim_apply_summary_media">
                  <el-image
                    class="sim_apply_cover"
                    :src="simulator.coverImageUrl"
                    fit="contain"
                    :preview-src-list="simulator.coverImageUrl ? [simulator.coverImageUrl] : []"
                  >
                    <template #error>
                      <div class="sim_apply_cover_placeholder">
                        <i class="uil uil-image" />
                        <span>Sin imagen</span>
                      </div>
                    </template>
                  </el-image>
                  <div class="sim_apply_summary_cta">
                    <!--
                      Examen ya cerrado: se ofrece consultar el resultado guardado.
                      El cierre de un examen contestado por completo se hace desde
                      el botón "Finalizar y ver resultados" (abajo), que además
                      pide confirmación.
                    -->
                    <button
                      v-if="simulator.isDone"
                      type="button"
                      class="account_btn_primary"
                      @click="onViewResults"
                    >
                      <i class="uil uil-chart-bar" /> Ver resultados
                    </button>
                    <button
                      v-else-if="!timerLabel && simulator.isTimer"
                      type="button"
                      class="account_btn_primary"
                      @click="onStartTimer"
                    >
                      <i class="uil uil-play" /> Iniciar simulador
                    </button>
                  </div>
                </div>

                <div class="sim_apply_summary_body">
                  <h4 class="sim_apply_summary_title">
                    Información del simulador
                  </h4>
                  <p
                    v-if="simulator.description"
                    class="sim_apply_summary_desc"
                  >
                    {{ simulator.description }}
                  </p>

                  <div class="sim_apply_facts">
                    <div class="sim_apply_fact">
                      <span class="sim_apply_fact_label">Estudiante</span>
                      <span class="sim_apply_fact_value">
                        {{ sessionInfo ? `${sessionInfo.user.firstName} ${sessionInfo.user.lastName}` : '—' }}
                      </span>
                    </div>
                    <div class="sim_apply_fact">
                      <span class="sim_apply_fact_label">N.º de preguntas</span>
                      <span class="sim_apply_fact_value">
                        {{ totalQuestions || simulator.questionQuantity }}
                      </span>
                    </div>
                    <div class="sim_apply_fact">
                      <span class="sim_apply_fact_label">Duración</span>
                      <span class="sim_apply_fact_value">
                        {{ simulator.duration }} min
                      </span>
                    </div>
                    <div class="sim_apply_fact">
                      <span class="sim_apply_fact_label">Temporizado</span>
                      <span class="sim_apply_fact_value">
                        {{ simulator.isTimer ? 'Sí' : 'No' }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Aviso de estado del examen -->
              <div
                v-if="!sectionSelected"
                class="sim_apply_notice"
              >
                <i class="uil uil-info-circle" />
                <span v-if="simulator.isDone">
                  Ya completaste este simulador. Puedes consultar tu resultado las veces que necesites.
                </span>
                <span v-else-if="canViewResults">
                  Ya contestaste las {{ totalQuestions }} preguntas de este simulador.
                  Puedes ver tu resultado o seguir revisando tus respuestas.
                </span>
                <span v-else-if="answeredQuantity > 0">
                  Llevas {{ answeredQuantity }} de {{ totalQuestions }} preguntas contestadas.
                </span>
                <span v-else-if="!timerLabel">
                  Para comenzar tu simulador, haz clic en «Iniciar simulador».
                </span>
                <span v-else>
                  Haz clic en la sección que desees comenzar a responder.
                </span>
              </div>

              <!-- Secciones disponibles -->
              <div
                v-if="!sectionSelected && !simulator.isDone"
                class="sim_apply_sections"
              >
                <div class="dash_section_header">
                  <div class="dash_section_title">
                    <i class="uil uil-layer-group" /> Secciones
                    <span class="dash_section_count">
                      {{ (simulator.simulatorSections || []).length }}
                    </span>
                  </div>
                </div>

                <div class="sim_apply_section_list">
                  <button
                    v-for="(section, sectionIndex) in (simulator.simulatorSections || [])"
                    :key="section.id"
                    type="button"
                    class="sim_apply_section"
                    @click="onSelectSection(section, sectionIndex)"
                  >
                    <span class="sim_apply_section_icon">
                      <i class="uil uil-angle-right" />
                    </span>

                    <span class="sim_apply_section_body">
                      <span class="sim_apply_section_name">
                        {{ section.name }}
                      </span>
                      <span
                        v-if="section.description"
                        class="sim_apply_section_desc"
                      >
                        {{ section.description }}
                      </span>
                    </span>

                    <span
                      class="sim_apply_section_status"
                      :class="{ is_done: isSectionFinished(section) }"
                    >
                      <i
                        :class="isSectionFinished(section) ? 'uil uil-check-circle' : 'uil uil-clock'"
                      />
                      {{ isSectionFinished(section) ? 'Finalizada' : 'Pendiente' }}
                    </span>
                  </button>
                </div>
              </div>

              <!-- Sección en curso -->
              <div
                v-if="sectionSelected"
                class="sim_apply_active"
              >
                <simulator-detail-section-view
                  :simulator-id="simulator.id"
                  :current-section="sectionSelected"
                />
              </div>

              <!-- Acción de cierre -->
              <div
                v-if="allSectionFinished() && !simulator.isDone && !sectionSelected"
                class="sim_apply_actions"
              >
                <button
                  type="button"
                  class="account_btn_primary"
                  @click="onFinishSimulator"
                >
                  <i class="uil uil-check-circle" />
                  {{ simulator.isCompleted ? 'Finalizar y ver resultados' : 'Finalizar simulador' }}
                </button>
              </div>
            </template>

            <!-- Resultados -->
            <simulator-official-result-view
              v-if="simulator && showResultView"
              :simulator-id="simulator.id"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-loading.fullscreen.lock="isLoading" />
  </div>
</template>
